import type { AdminSortKey } from "../../lib/api";

/** A column's sort direction. */
export type SortDir = "asc" | "desc";

/** The active sort: which column and which direction. */
export interface SortState {
  key: AdminSortKey;
  dir: SortDir;
}

/** Descriptor for one sortable column header. */
export interface SortColumn {
  key: AdminSortKey;
  label: string;
  /** Direction applied on the first click of this column. */
  defaultDir: SortDir;
}

/**
 * Sortable columns keyed by sort key, each with the direction applied on the
 * first click. Text columns default ascending (A→Z); magnitude/date columns
 * default descending (biggest / most-recent first); TTL defaults ascending
 * (soonest-to-expire first). The table component owns header layout and looks
 * each column up here for its label, default direction, and active state.
 */
export const SORT_COLUMNS: Record<AdminSortKey, SortColumn> = {
  title: { key: "title", label: "Title", defaultDir: "asc" },
  owner: { key: "owner", label: "owner", defaultDir: "asc" },
  visibility: { key: "visibility", label: "Visibility", defaultDir: "asc" },
  size: { key: "size", label: "Size", defaultDir: "desc" },
  views: { key: "views", label: "Views", defaultDir: "desc" },
  last_viewed: { key: "last_viewed", label: "Last viewed", defaultDir: "desc" },
  ttl: { key: "ttl", label: "TTL", defaultDir: "asc" },
  updated: { key: "updated", label: "Updated", defaultDir: "desc" },
};

/** The page's initial sort — newest-`updated` first (prior default behavior). */
export const DEFAULT_SORT: SortState = { key: "updated", dir: "desc" };

/**
 * Compute the next sort state when a column header is clicked.
 *
 * Clicking the active column flips its direction; clicking a different column
 * switches to it using that column's configured default direction.
 *
 * @param current - The currently active sort.
 * @param clickedKey - The column header that was clicked.
 * @returns The next sort state.
 */
export function nextSort(current: SortState, clickedKey: AdminSortKey): SortState {
  if (current.key === clickedKey) {
    return { key: clickedKey, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key: clickedKey, dir: SORT_COLUMNS[clickedKey].defaultDir };
}
